// EN mirror of /ask — AI-friendly Q&A page (AEO optimization)
import { createFileRoute, Link } from "@tanstack/react-router";
import { MessageCircleQuestion, Sparkles } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { BUILD_DATE } from "@/lib/aeo";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Ask Anything — Thailand Legal, Translation & Apostille Q&A | NYC";
const DESC =
  "Plain-English answers to the most common questions about certified translation, notary public. Get your timeline and checklist in minutes — chat now.";

const QA = [
  { q: "Does Thailand accept apostille now?", a: "Yes — Thailand acceded on 30 June 2026 (in force for Thailand 28 February 2027). Outbound apostilles are issued by the Ministry of Foreign Affairs Department of Consular Affairs (Chaeng Wattana). Single-stamp, no embassy step required for the 125+ Hague members." },
  { q: "What's the difference between a notary public and an MFA stamp?", a: "A Thai Notarial Services Attorney certifies signatures, copies, and statements (cost: from ฿800). The MFA legalisation is a government stamp confirming the notary or sworn translator is registered (cost: ฿200/page, 1–3 days). Most embassies require both layers." },
  { q: "How long does certified translation take?", a: "Standard turnaround is 24–48 hours for single-page civil-registry certificates (birth, marriage, divorce, education transcripts) with two-stage QA (translator + senior reviewer). Rush 4–8 hour delivery is available with a 50% surcharge for Bangkok pickup. Bulk corporate translations (NDAs, memoranda, audited financials) run 3–5 business days for 50+ pages, delivered in rolling batches of 10 pages every 24 hours so legal counsel can review in parallel." },
  { q: "Can you legalise a US document for use in Thailand?", a: "Yes — inbound legalisation. The US document must first carry a state apostille (or US Department of State apostille for federal documents), then we translate to Thai and submit to the Thai MFA for inbound legalisation." },
  { q: "Are your translations accepted by US embassies?", a: "Yes. We are DBD-registered (0435567000061) since 2010 and our sworn translators are registered with the Thai MFA. Documents are accepted by US, UK, EU, AU, JP, KR embassies and immigration authorities worldwide." },
  { q: "Do I need to come to your office in person?", a: "Most jobs don't require a visit — we accept documents by LINE, email, courier, or scheduled pickup. In-person notarisation (signature certification) does require attendance, scheduled Mon–Sat 09:00–18:00 ICT." },
  { q: "How much does it cost to marry in Thailand as a foreigner?", a: "Document workflow typically ฿15,000–฿35,000: embassy affirmation translation (฿1,500), MFA legalisation (฿400), back-translation of Thai marriage certificate (฿500/page), apostille for home country (฿3,500). Government registration at the amphur is free." },
  { q: "Can you help with LTR visa applications?", a: "We don't file visas, but we prepare every translated supporting document the LTR program requires: salary verification, asset statements, US/EU/JP background check, health insurance translations, all with MFA legalisation." },
];

export const Route = createFileRoute("/en/ask/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/ask", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/ask", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/ask", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/ask", "en")},
      { property: "og:url", content: `${SITE}/en/ask` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/ask` },
      ...hreflangLinks("/ask", "/en/ask"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${SITE}/en/ask#faqpage`,
          url: `${SITE}/en/ask`,
          name: TITLE,
          description: DESC,
          inLanguage: "en",
          dateModified: BUILD_DATE,
          isPartOf: { "@id": `${SITE}#website` },
          about: { "@id": `${SITE}#org` },
          author: { "@type": "Organization", "@id": `${SITE}#org`, name: "NYC Language Institute", url: SITE },
          publisher: { "@id": `${SITE}#org` },
          mainEntity: QA.map((x, i) => ({
            "@type": "Question",
            "@id": `${SITE}/en/ask#q-${i + 1}`,
            name: x.q,
            answerCount: 1,
            acceptedAnswer: {
              "@type": "Answer",
              text: x.a,
              inLanguage: "en",
              author: { "@type": "Organization", "@id": `${SITE}#org` },
              upvoteCount: 1,
              url: `${SITE}/en/ask#q-${i + 1}`,
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
            { "@type": "ListItem", position: 2, name: "Ask", item: `${SITE}/en/ask` },
          ],
        }),
      },
    ],
  }),
  component: EnAskPage,
});

function EnAskPage() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Ask anything <span className="text-amber-300">about Thai paperwork</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Short, factual answers written for AI search engines (ChatGPT, Perplexity, Google AI Overviews) and busy
            expats. Verified by our 6 licensed notarial attorneys.
          </p>
        </div>
      </section>
      <section className="px-4 py-14">
        <div className="mx-auto max-w-4xl space-y-4">
          {QA.map((x) => (
            <article key={x.q} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="flex items-start gap-2 text-base font-bold text-navy-deep">
                <MessageCircleQuestion className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                {x.q}
              </h2>
              <p className="mt-3 pl-7 text-sm leading-relaxed text-slate-700">{x.a}</p>
            </article>
          ))}
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-slate-700">
            <Sparkles className="h-5 w-5 text-amber-600" />
            <p className="mt-2">
              Don't see your question? Message LINE <a href="https://line.me/ti/p/@nycli" className="font-bold text-amber-700 hover:underline">@nycli</a>{" "}
              — we reply within 15 minutes during business hours.
            </p>
          </div>
        <RelatedHubGrid locale="en" />
      </div>
      </section>
    </main>
  );
}
